import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { 
  getReimbursements, createReimbursement, approveReimbursement, 
  rejectReimbursement, getReimbursementStats, uploadReimbursementReceipt,
  getEmployees
} from '../api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import { 
  Receipt, Plus, CheckCircle, XCircle, Clock, IndianRupee, 
  Upload, FileText, Car, Heart, Utensils, Phone, Home, HelpCircle,
  Eye, Download, AlertCircle, Search, Filter
} from 'lucide-react';
import { toast } from 'sonner';

const REIMBURSEMENT_LIMIT = 5000;

const CATEGORY_CONFIG = {
  travel: { label: 'Travel/Conveyance', icon: Car, color: 'bg-blue-100 text-blue-800' },
  medical: { label: 'Medical', icon: Heart, color: 'bg-red-100 text-red-800' },
  food: { label: 'Food/Meals', icon: Utensils, color: 'bg-amber-100 text-amber-800' },
  communication: { label: 'Communication', icon: Phone, color: 'bg-purple-100 text-purple-800' },
  work_from_home: { label: 'Work from Home', icon: Home, color: 'bg-green-100 text-green-800' },
  other: { label: 'Other', icon: HelpCircle, color: 'bg-slate-100 text-slate-800' }
};

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-800', icon: Clock },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800', icon: XCircle }
};

const Reimbursements = ({ user, setUser }) => {
  const [reimbursements, setReimbursements] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedReimbursement, setSelectedReimbursement] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [actionRemarks, setActionRemarks] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [employees, setEmployees] = useState([]);

  const [formData, setFormData] = useState({
    category: '',
    amount: '',
    description: '',
    expense_date: new Date().toISOString().split('T')[0],
    receipt: null
  });

  const isAdminOrHR = user?.role === 'admin' || user?.role === 'hr_manager' || user?.role === 'accounts_manager';

  useEffect(() => {
    fetchReimbursements();
    if (isAdminOrHR) {
      fetchStats();
      fetchEmployees();
    }
  }, [filterStatus]);

  const fetchReimbursements = async () => {
    try {
      const status = filterStatus !== 'all' ? filterStatus : null;
      const response = await getReimbursements(status);
      setReimbursements(response.data);
    } catch (error) {
      toast.error('Failed to fetch reimbursements');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await getReimbursementStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await getEmployees();
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category || !formData.amount || !formData.description || !formData.expense_date) {
      toast.error('Please fill all required fields');
      return;
    }

    if (parseFloat(formData.amount) <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }

    setSubmitting(true);
    try {
      const response = await createReimbursement({
        category: formData.category,
        amount: parseFloat(formData.amount),
        description: formData.description,
        expense_date: formData.expense_date
      });

      // If there's a receipt file, upload it
      if (formData.receipt) {
        try {
          await uploadReimbursementReceipt(response.data.id, formData.receipt);
        } catch (uploadError) {
          toast.warning('Reimbursement created but receipt upload failed');
        }
      }

      toast.success('Reimbursement request submitted successfully');
      setCreateDialogOpen(false);
      setFormData({
        category: '',
        amount: '',
        description: '',
        expense_date: new Date().toISOString().split('T')[0],
        receipt: null
      });
      fetchReimbursements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create reimbursement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAction = async () => {
    if (!selectedReimbursement) return;

    setSubmitting(true);
    try {
      if (actionType === 'approve') {
        await approveReimbursement(selectedReimbursement.id, actionRemarks);
        toast.success('Reimbursement approved');
      } else {
        await rejectReimbursement(selectedReimbursement.id, actionRemarks);
        toast.success('Reimbursement rejected');
      }
      setActionDialogOpen(false);
      setSelectedReimbursement(null);
      setActionRemarks('');
      fetchReimbursements();
      if (isAdminOrHR) fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${actionType} reimbursement`);
    } finally {
      setSubmitting(false);
    }
  };

  const openActionDialog = (reimbursement, type) => {
    setSelectedReimbursement(reimbursement);
    setActionType(type);
    setActionRemarks('');
    setActionDialogOpen(true);
  };

  const openViewDialog = (reimbursement) => {
    setSelectedReimbursement(reimbursement);
    setViewDialogOpen(true);
  };

  const filteredReimbursements = reimbursements.filter(r => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        r.employee_name?.toLowerCase().includes(query) ||
        r.description?.toLowerCase().includes(query) ||
        CATEGORY_CONFIG[r.category]?.label.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const getCategoryIcon = (category) => {
    const config = CATEGORY_CONFIG[category];
    if (!config) return HelpCircle;
    return config.icon;
  };

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Reimbursements</h1>
            <p className="text-slate-600 mt-1">
              {isAdminOrHR 
                ? 'Manage and approve employee reimbursement requests'
                : 'Submit and track your expense reimbursement requests'
              }
            </p>
          </div>
          {!isAdminOrHR && (
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus size={18} className="mr-2" />
                  New Request
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Submit Reimbursement Request</DialogTitle>
                  <DialogDescription>
                    Fill in the details of your expense. Amounts above ₹{REIMBURSEMENT_LIMIT.toLocaleString()} require Admin approval.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="category">Category *</Label>
                    <Select 
                      value={formData.category} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                          <SelectItem key={key} value={key}>
                            <div className="flex items-center gap-2">
                              <config.icon size={16} />
                              {config.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="amount">Amount (₹) *</Label>
                      <Input
                        id="amount"
                        type="number"
                        value={formData.amount}
                        onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                        placeholder="Enter amount"
                        min="1"
                        step="0.01"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="expense_date">Expense Date *</Label>
                      <Input
                        id="expense_date"
                        type="date"
                        value={formData.expense_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, expense_date: e.target.value }))}
                        max={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description *</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Describe the expense details..."
                      rows={3}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="receipt">Receipt (Optional)</Label>
                    <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center">
                      <input
                        id="receipt"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => setFormData(prev => ({ ...prev, receipt: e.target.files[0] }))}
                        className="hidden"
                      />
                      <label htmlFor="receipt" className="cursor-pointer">
                        <Upload className="mx-auto h-8 w-8 text-slate-400 mb-2" />
                        <p className="text-sm text-slate-600">
                          {formData.receipt ? formData.receipt.name : 'Click to upload receipt'}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">PDF, JPG, PNG (max 5MB)</p>
                      </label>
                    </div>
                  </div>

                  {parseFloat(formData.amount) > REIMBURSEMENT_LIMIT && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                      <AlertCircle className="text-amber-600 mt-0.5" size={18} />
                      <p className="text-sm text-amber-800">
                        Amounts above ₹{REIMBURSEMENT_LIMIT.toLocaleString()} require Admin approval
                      </p>
                    </div>
                  )}

                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700">
                      {submitting ? 'Submitting...' : 'Submit Request'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Stats Cards - Admin/HR Only */}
        {isAdminOrHR && stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Pending</p>
                    <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
                  </div>
                  <Clock className="h-8 w-8 text-amber-400" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Approved</p>
                    <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-400" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Rejected</p>
                    <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
                  </div>
                  <XCircle className="h-8 w-8 text-red-400" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Monthly Approved</p>
                    <p className="text-2xl font-bold text-indigo-600">
                      ₹{stats.monthly_approved_amount?.toLocaleString() || 0}
                    </p>
                  </div>
                  <IndianRupee className="h-8 w-8 text-indigo-400" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
                <Input
                  placeholder={isAdminOrHR ? "Search by employee name or description..." : "Search by description..."}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter size={18} className="text-slate-400" />
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reimbursements List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="text-indigo-600" />
              {isAdminOrHR ? 'All Reimbursement Requests' : 'Your Reimbursement Requests'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredReimbursements.length === 0 ? (
              <div className="text-center py-12">
                <Receipt className="mx-auto h-12 w-12 text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-600">No reimbursements found</h3>
                <p className="text-slate-500 mt-1">
                  {!isAdminOrHR && "Submit your first reimbursement request using the button above."}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredReimbursements.map((reimbursement) => {
                  const CategoryIcon = getCategoryIcon(reimbursement.category);
                  const categoryConfig = CATEGORY_CONFIG[reimbursement.category] || CATEGORY_CONFIG.other;
                  const statusConfig = STATUS_CONFIG[reimbursement.status] || STATUS_CONFIG.pending;
                  const StatusIcon = statusConfig.icon;

                  return (
                    <div 
                      key={reimbursement.id}
                      className="border rounded-lg p-4 hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className={`p-2 rounded-lg ${categoryConfig.color}`}>
                            <CategoryIcon size={20} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-slate-900">
                                {categoryConfig.label}
                              </h4>
                              <Badge className={statusConfig.color}>
                                <StatusIcon size={12} className="mr-1" />
                                {statusConfig.label}
                              </Badge>
                              {reimbursement.amount > REIMBURSEMENT_LIMIT && (
                                <Badge variant="outline" className="border-amber-300 text-amber-700">
                                  Admin Approval Required
                                </Badge>
                              )}
                            </div>
                            {isAdminOrHR && (
                              <p className="text-sm text-slate-600 mt-0.5">
                                {reimbursement.employee_name}
                              </p>
                            )}
                            <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                              {reimbursement.description}
                            </p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                              <span>Expense Date: {reimbursement.expense_date}</span>
                              <span>Submitted: {new Date(reimbursement.created_at).toLocaleDateString()}</span>
                            </div>
                            {reimbursement.status !== 'pending' && reimbursement.approval_remarks && (
                              <p className="text-sm text-slate-500 mt-2 italic">
                                Remarks: {reimbursement.approval_remarks}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-slate-900">
                            ₹{reimbursement.amount.toLocaleString()}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => openViewDialog(reimbursement)}
                            >
                              <Eye size={14} className="mr-1" />
                              View
                            </Button>
                            {isAdminOrHR && reimbursement.status === 'pending' && (
                              <>
                                <Button 
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => openActionDialog(reimbursement, 'approve')}
                                >
                                  <CheckCircle size={14} className="mr-1" />
                                  Approve
                                </Button>
                                <Button 
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => openActionDialog(reimbursement, 'reject')}
                                >
                                  <XCircle size={14} className="mr-1" />
                                  Reject
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* View Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Reimbursement Details</DialogTitle>
            </DialogHeader>
            {selectedReimbursement && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-500">Category</Label>
                    <p className="font-medium">
                      {CATEGORY_CONFIG[selectedReimbursement.category]?.label || selectedReimbursement.category}
                    </p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Amount</Label>
                    <p className="font-medium text-lg">₹{selectedReimbursement.amount.toLocaleString()}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Status</Label>
                    <Badge className={STATUS_CONFIG[selectedReimbursement.status]?.color}>
                      {STATUS_CONFIG[selectedReimbursement.status]?.label}
                    </Badge>
                  </div>
                  <div>
                    <Label className="text-slate-500">Expense Date</Label>
                    <p className="font-medium">{selectedReimbursement.expense_date}</p>
                  </div>
                </div>

                {isAdminOrHR && (
                  <div>
                    <Label className="text-slate-500">Employee</Label>
                    <p className="font-medium">{selectedReimbursement.employee_name}</p>
                  </div>
                )}

                <div>
                  <Label className="text-slate-500">Description</Label>
                  <p className="mt-1 text-slate-700">{selectedReimbursement.description}</p>
                </div>

                {selectedReimbursement.receipt_url && (
                  <div>
                    <Label className="text-slate-500">Receipt</Label>
                    <div className="mt-2">
                      {selectedReimbursement.receipt_url.startsWith('data:image') ? (
                        <img 
                          src={selectedReimbursement.receipt_url} 
                          alt="Receipt" 
                          className="max-w-full max-h-64 rounded-lg border"
                        />
                      ) : (
                        <a 
                          href={selectedReimbursement.receipt_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-800"
                        >
                          <FileText size={18} />
                          View Receipt Document
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {selectedReimbursement.status !== 'pending' && (
                  <div className="border-t pt-4 space-y-2">
                    <div className="flex justify-between">
                      <Label className="text-slate-500">Processed By</Label>
                      <p className="font-medium">{selectedReimbursement.approved_by_name}</p>
                    </div>
                    {selectedReimbursement.approved_at && (
                      <div className="flex justify-between">
                        <Label className="text-slate-500">Processed On</Label>
                        <p className="font-medium">
                          {new Date(selectedReimbursement.approved_at).toLocaleString()}
                        </p>
                      </div>
                    )}
                    {selectedReimbursement.approval_remarks && (
                      <div>
                        <Label className="text-slate-500">Remarks</Label>
                        <p className="mt-1 text-slate-700 italic">
                          {selectedReimbursement.approval_remarks}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Action Dialog (Approve/Reject) */}
        <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {actionType === 'approve' ? 'Approve Reimbursement' : 'Reject Reimbursement'}
              </DialogTitle>
              <DialogDescription>
                {actionType === 'approve' 
                  ? 'Confirm approval of this reimbursement request.'
                  : 'Please provide a reason for rejection.'
                }
              </DialogDescription>
            </DialogHeader>
            {selectedReimbursement && (
              <div className="space-y-4">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium">{selectedReimbursement.employee_name}</p>
                      <p className="text-sm text-slate-600">
                        {CATEGORY_CONFIG[selectedReimbursement.category]?.label}
                      </p>
                    </div>
                    <p className="text-xl font-bold">₹{selectedReimbursement.amount.toLocaleString()}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="remarks">Remarks {actionType === 'reject' && '*'}</Label>
                  <Textarea
                    id="remarks"
                    value={actionRemarks}
                    onChange={(e) => setActionRemarks(e.target.value)}
                    placeholder={actionType === 'approve' ? 'Optional remarks...' : 'Reason for rejection...'}
                    rows={3}
                  />
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setActionDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleAction}
                    disabled={submitting || (actionType === 'reject' && !actionRemarks.trim())}
                    className={actionType === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
                  >
                    {submitting ? 'Processing...' : (actionType === 'approve' ? 'Approve' : 'Reject')}
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Reimbursements;
